import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Clipboard, Edit2Icon, Save } from "lucide-react";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { stripMarkdown } from "@/lib/utils";
import MarkdownEditor from "@/components/ui/EditorMarkdown";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "@/hooks/use-toast";
import { t } from "i18next";
import type { NoteTemplate } from "@/common/types/notesTemplates";
import { CardContentTemplate } from "@/components/ui/CardTemplate";

interface Props {
  file: NoteTemplate;
  onSave: (result: string) => void;
  loading: boolean;
  tools?: React.ReactNode;
}

export const CardContentTemplateGenerated = (props: Props): React.ReactNode => {
  const [isEditingResult, setIsEditingResult] = useState(false);
  const [newValue, setNewValue] = useState(props.file.result || "");

  const handleCopyClipboard = async (
    text: string | undefined
  ): Promise<void> => {
    if (text != undefined) {
      try {
        await navigator.clipboard.writeText(stripMarkdown(text));
        toast({
          variant: "default",
          description: t("visits.templateViewer.messages.textCopied"),
          duration: 1000,
        });
      } catch {
        // silent fail
      }
    }
  };

  return (
    <CardContentTemplate file={props.file}>
      <div className="space-y-4">
        <div className="flex justify-between">
          <div></div>
          <div className="flex gap-2">
            {props.tools}
            {isEditingResult ? (
              <>
                <Button
                  disabled={props.loading}
                  size="sm"
                  variant="success"
                  onClick={() => {
                    props.onSave(newValue);
                  }}
                >
                  {props.loading && <LoadingSpinner />}
                  <Save />
                  {t("common.save")}
                </Button>
                <Button
                  size="sm"
                  variant="grey"
                  onClick={() => {
                    setIsEditingResult(false);
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsEditingResult(true);
                  }}
                >
                  {t("visits.templateViewer.buttons.edit")} <Edit2Icon />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleCopyClipboard(props.file.result)}
                >
                  {t("visits.templateViewer.buttons.copyClipboard")}{" "}
                  <Clipboard />
                </Button>
              </>
            )}
          </div>
        </div>
        <div>
          {isEditingResult ? (
            <MarkdownEditor
              initValue={props.file.result || ""}
              onChange={setNewValue}
            />
          ) : (
            <FileResultRendererMD result={props.file.result || ""} />
          )}
        </div>
      </div>
    </CardContentTemplate>
  );
};
